// eslint-disable-next-line max-classes-per-file
import { printInHourDisplayFormat } from './dateTimePrinting';

class AbstractWarning {
  showAsInvalid() {
    return false;
  }

  showAsInvalidOnStartTime() {
    return this.showAsInvalid();
  }

  showAsInvalidOnEndTime() {
    return this.showAsInvalid();
  }

  isInvalidTimeWarning() {
    return false;
  }

  isError() {
    return this.getVariant() === 'error';
  }

  isInfo() {
    return this.getVariant() === 'info';
  }

  showWarningWhenTimesheetIsNotEditable() {
    return !this.isInfo();
  }

  isChangedAfterApprovalError() {
    return false;
  }
}

/* ________________________________ */
/*                                  */
/* Warnings related to actual times */
/*                                  */
/* ________________________________ */

class AbstractWarningOnYPTimesheetEntryObject extends AbstractWarning {
  constructor(entry) {
    super();
    this.entry = entry;
  }

  getActualStartTimeInHourDisplayFormat() {
    return printInHourDisplayFormat(this.entry.getActualStart());
  }

  getActualEndTimeInHourDisplayFormat() {
    return printInHourDisplayFormat(this.entry.getActualEnd());
  }

  getTimesheetStartTimeInHourDisplayFormat() {
    return printInHourDisplayFormat(this.entry.getTimesheetStart());
  }

  getTimesheetEndTimeInHourDisplayFormat() {
    return printInHourDisplayFormat(this.entry.getTimesheetEnd());
  }
}

class AbstractActualTimesWarning extends AbstractWarningOnYPTimesheetEntryObject {
  constructor(entry, forStartTime, forEndTime) {
    super(entry);
    this.forStartTime = forStartTime;
    this.forEndTime = forEndTime;
  }

  isForStartTime() {
    return this.forStartTime && !this.forEndTime;
  }

  isForEndTime() {
    return this.forEndTime && !this.forStartTime;
  }

  isForStartAndEndTime() {
    return this.forStartTime && this.forEndTime;
  }
}

export class ActualTimesBeforeSubmittingWarning extends AbstractWarningOnYPTimesheetEntryObject {
  getWarningText() {
    const actualStart = this.getActualStartTimeInHourDisplayFormat();
    const actualEnd = this.getActualEndTimeInHourDisplayFormat();
    return [i18n`Actual times have been added: ${actualStart}–${actualEnd}.`];
  }

  getVariant() {
    return 'info';
  }
}

export class ActualTimesDifferAfterSubmittingWarning extends AbstractActualTimesWarning {
  getWarningTextForStartTime() {
    const actualTime = this.getActualStartTimeInHourDisplayFormat();
    return i18n`Actual start time has been changed to ${actualTime}.`;
  }

  getWarningTextForEndTime() {
    const actualTime = this.getActualEndTimeInHourDisplayFormat();
    return i18n`Actual end time has been changed to ${actualTime}.`;
  }

  getWarningText() {
    const warningTextForStart = this.getWarningTextForStartTime();
    const warningTextForEnd = this.getWarningTextForEndTime();
    if (this.isForStartAndEndTime()) return [warningTextForStart, warningTextForEnd];
    if (this.isForStartTime()) return [warningTextForStart];
    return [warningTextForEnd];
  }

  getVariant() {
    return 'error';
  }

  isChangedAfterApprovalError() {
    return true;
  }
}

export class BookingAddedAfterSubmittingWarning extends AbstractWarningOnYPTimesheetEntryObject {
  getWarningText() {
    if (this.entry.isResourceBooking()) return [i18n`A booking has been added.`];
    if (this.entry.isBreak()) return [i18n`A break has been added.`];
    if (this.entry.isShift()) return [i18n`A shift has been added.`];
    return [];
  }

  getVariant() {
    return 'error';
  }

  isChangedAfterApprovalError() {
    return true;
  }
}

export class BookingWithActualTimesAddedAfterSubmittingWarning extends AbstractWarningOnYPTimesheetEntryObject {
  getWarningText() {
    const actualStart = this.getActualStartTimeInHourDisplayFormat();
    const actualEnd = this.getActualEndTimeInHourDisplayFormat();
    if (this.entry.isResourceBooking())
      return [
        i18n`A booking has been added with actual times ${actualStart}–${actualEnd}.`
      ];
    if (this.entry.isBreak())
      return [
        i18n`A break has been added with actual times ${actualStart}–${actualEnd}.`
      ];
    if (this.entry.isShift())
      return [
        i18n`A shift has been added with actual times ${actualStart}–${actualEnd}.`
      ];
    return [];
  }

  getVariant() {
    return 'error';
  }

  isChangedAfterApprovalError() {
    return true;
  }
}

export class NoActualTimesInFinalStateWarning extends AbstractWarningOnYPTimesheetEntryObject {
  getWarningText() {
    const timesheetStartTime = this.getTimesheetStartTimeInHourDisplayFormat();
    const timesheetEndTime = this.getTimesheetEndTimeInHourDisplayFormat();
    return [
      i18n`Submitted times ${timesheetStartTime}–${timesheetEndTime} have been removed.`
    ];
  }

  getVariant() {
    return 'error';
  }

  isChangedAfterApprovalError() {
    return true;
  }
}

export class ActualStateAbstractWarning extends AbstractWarningOnYPTimesheetEntryObject {
  getVariant() {
    return 'info';
  }
}
export class ActualStateSickLeaveWarning extends ActualStateAbstractWarning {
  getWarningText() {
    return [i18n`The actual status has been changed to Sick Leave.`];
  }

  consistentShiftStartWarning() {
    return true;
  }
}
export class ActualStateCancelledWarning extends ActualStateAbstractWarning {
  getWarningText() {
    return [i18n`The actual status has been changed to Cancelled.`];
  }

  consistentShiftStartWarning() {
    return true;
  }
}
export class ActualStateSickLeaveAfterFinalStateWarning extends ActualStateAbstractWarning {
  getWarningText() {
    if (this.entry.isBreak())
      return [
        i18n`The actual status of the break has been changed to Sick Leave: planned times are used for working time.`
      ];
    if (this.entry.isShift())
      return [
        i18n`The actual status of the shift has been changed to Sick Leave: planned times are used for working time.`
      ];
    return [];
  }

  isChangedAfterApprovalError() {
    return true;
  }

  showWarningWhenTimesheetIsNotEditable() {
    return true;
  }
}
export class ActualStateCancelledAfterFinalStateWarning extends ActualStateAbstractWarning {
  getWarningText() {
    if (this.entry.isBreak())
      return [
        i18n`The actual status of the break has been changed to Cancelled: times are not counted as working time.`
      ];
    if (this.entry.isShift())
      return [
        i18n`The actual status of the shift has been changed to Cancelled: times are not counted as working time.`
      ];
    return [];
  }

  isChangedAfterApprovalError() {
    return true;
  }

  showWarningWhenTimesheetIsNotEditable() {
    return true;
  }
}

/* ___________________________________ */
/*                                     */
/* Warnings related to timesheet times */
/*                                     */
/* ___________________________________ */

export class OverlapOnBreakWarning extends AbstractWarning {
  getWarningText() {
    return [i18n`Break overlaps with booking.`];
  }

  getVariant() {
    return 'info';
  }
}

class AbstractShiftWarning extends AbstractWarning {
  constructor(bookingEntry) {
    super();
    this.booking = bookingEntry;
  }

  showAsInvalid() {
    return true;
  }

  getVariant() {
    return 'error';
  }
}

export class ShiftStartsLaterThanBookingWarning extends AbstractShiftWarning {
  getWarningText() {
    const time = printInHourDisplayFormat(this.booking.getTimesheetStart());
    return [i18n`Earliest booking starts at ${time}.`];
  }
}

export class ShiftEndsEarlierThanBookingWarning extends AbstractShiftWarning {
  getWarningText() {
    const time = printInHourDisplayFormat(this.booking.getTimesheetEnd());
    return [i18n`Latest booking ends at ${time}.`];
  }
}

export class EndTimeBeforeStartTimeWarning extends AbstractWarning {
  getWarningText() {
    return [i18n`End time can’t come before start time.`];
  }

  getVariant() {
    return 'error';
  }

  showAsInvalid() {
    return true;
  }

  showAsInvalidOnStartTime() {
    return false;
  }

  isInvalidTimeWarning() {
    return true;
  }
}

export class StartTimeAfterEndTimeWarning extends AbstractWarning {
  getWarningText() {
    return [i18n`Start time can’t come after end time.`];
  }

  getVariant() {
    return 'error';
  }

  showAsInvalid() {
    return true;
  }

  showAsInvalidOnEndTime() {
    return false;
  }

  isInvalidTimeWarning() {
    return true;
  }
}

export class InvalidDurationOfTimesheetItemWarning extends AbstractWarning {
  getWarningText() {
    return [i18n`Start and end times may at most be a day apart.`];
  }

  getVariant() {
    return 'error';
  }

  showAsInvalid() {
    return true;
  }

  isInvalidTimeWarning() {
    return true;
  }
}
